import React, { useState, useEffect, useRef } from "react";
import { Button, Col, Modal, Tabs, Tooltip } from "antd";
import "../css/modal.css";
import {
  DownloadOutlined,
  EyeOutlined,
  LoadingOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { Row } from "antd";

const { TabPane } = Tabs;

type RightSidePreviewModalProps = {
  handleCancel: any;
  open: boolean;
  documentUrl: string | undefined | null;
  stageDocuments?: any;
  handleDownloadDocument?: any;
  isDownloadingDocument?: boolean;
  downloadingDocumentUrl?: string;
  handleSetDocumentUrl?: any;
  id?: number;
  clickedActiveTab?: any;
};

const PatientListPreviewDocuments: React.FC<RightSidePreviewModalProps> = ({
  handleCancel,
  open,
  documentUrl,
  stageDocuments,
  handleDownloadDocument,
  handleSetDocumentUrl,
  isDownloadingDocument,
  downloadingDocumentUrl,
  clickedActiveTab,
  id,
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [documentKey, setDocumentKey] = useState<string>("");
  const [rotationAngle, setRotationAngle] = useState(0);
  const [activeTab, setActiveTab] = useState<any>("");

  const prevUrlRef = useRef<string | null | undefined>(null);

  useEffect(() => {
    if (documentUrl !== prevUrlRef.current) {
      setDocumentKey(documentUrl || `empty-${Date.now()}`);
      prevUrlRef.current = documentUrl;

      setZoomLevel(1);
    }
  }, [documentUrl]);

  useEffect(() => {
    if (clickedActiveTab) {
      setActiveTab(clickedActiveTab);
    }
  }, [clickedActiveTab]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    const firstDocUrl = stageDocuments?.[key]?.[0]?.documentUrl;
    if (firstDocUrl && firstDocUrl !== documentUrl) {
      handleSetDocumentUrl(firstDocUrl);
    }
  };

  useEffect(() => {
    setZoomLevel(1);
    setRotationAngle(0);
  }, [documentUrl]);

  const isPdf = documentUrl?.toLowerCase().endsWith(".pdf");

  const handleZoomIn = () => {
    setZoomLevel((prevZoom) => Math.min(prevZoom + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
  };

  const handleRotateLeft = () => setRotationAngle((prev) => prev - 90);
  const handleRotateRight = () => setRotationAngle((prev) => prev + 90);

  return (
    <>
      {documentUrl && (
        <div className="patient-list-right-side-modal">
          <Modal
            open={open}
            width={4000}
            onCancel={handleCancel}
            footer={(_) => (
              <div
                style={{
                  display: "flex",
                  justifyContent: isPdf ? "flex-end" : "space-between",
                }}
              >
                {!isPdf ? (
                  <div className="zoom-controls">
                    <Button
                      onClick={handleZoomOut}
                      disabled={zoomLevel <= 0.5}
                      style={{ marginRight: "0.5rem" }}
                    >
                      <ZoomOutOutlined />
                    </Button>
                    <Button
                      style={{ marginRight: "0.5rem" }}
                      onClick={handleZoomIn}
                      disabled={zoomLevel >= 3}
                    >
                      <ZoomInOutlined />
                    </Button>
                    <Button
                      style={{ marginRight: "0.5rem" }}
                      onClick={handleRotateLeft}
                    >
                      <RotateLeftOutlined />
                    </Button>
                    <Button
                      style={{ marginRight: "0.5rem" }}
                      onClick={handleRotateRight}
                    >
                      <RotateRightOutlined />
                    </Button>
                  </div>
                ) : (
                  ""
                )}
                <Button key="back" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            )}
            style={{ top: 8, right: 4, left: 4, height: "108vh", margin: 0 }}
            wrapClassName="patient-list-right-side-modal"
          >
            <Tabs
              // defaultActiveKey="0"
              activeKey={activeTab}
              onChange={handleTabChange}
              type="card"
            >
              {stageDocuments && Object.keys(stageDocuments)?.map(
                (stage: string, index: number) =>
                  stageDocuments[stage]?.length > 0 ? (
                    <TabPane active={activeTab} tab={stage} key={stage}>
                      <Row gutter={[16, 16]} style={{ padding: "6px" }}>
                        <Col
                          span={24}
                          style={{
                            display: "flex",
                            gap: "16px",
                            flexWrap: "wrap",
                            paddingLeft: 0,
                          }}
                        >
                          {stageDocuments[stage]?.map(
                            (document: any, idx: number) => (
                              <div
                                key={idx}
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  gap: "12px",
                                  border:
                                    documentUrl === document?.documentUrl
                                      ? "1px solid rgb(0, 84, 157)"
                                      : "1px solid #ddd",
                                  color:
                                    documentUrl === document?.documentUrl
                                      ? "white"
                                      : "inherit",
                                  background:
                                    documentUrl === document?.documentUrl
                                      ? "rgb(0, 84, 157)"
                                      : "inherit",
                                  padding: "8px",
                                  borderRadius: "8px",
                                  fontSize: "0.8rem",
                                }}
                              >
                                <span>
                                  <Tooltip
                                    title={
                                      document?.documentLabelType ||
                                      document?.documentName
                                    }
                                  >
                                    {document?.documentLabelType
                                      ? document?.documentLabelType?.length > 10
                                        ? document?.documentLabelType?.substring(
                                            0,
                                            10
                                          ) + "..."
                                        : document?.documentLabelType
                                      : document?.documentName &&
                                        document?.documentName?.length > 10
                                      ? document?.documentName?.substring(
                                          0,
                                          10
                                        ) + "..."
                                      : document?.documentName}
                                  </Tooltip>
                                </span>
                                <div
                                  className="icons"
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: "12px",
                                    cursor: "pointer",
                                  }}
                                >
                                  <Tooltip title="VIEW" color="blue">
                                    <EyeOutlined
                                      style={{
                                        color:
                                          documentUrl === document?.documentUrl
                                            ? "white"
                                            : "#273881",
                                        fontSize: "0.8rem",
                                      }}
                                      onClick={() =>
                                        handleSetDocumentUrl(
                                          document?.documentUrl
                                        )
                                      }
                                    />
                                  </Tooltip>
                                  {isDownloadingDocument &&
                                  downloadingDocumentUrl ===
                                    document?.documentUrl ? (
                                    <LoadingOutlined />
                                  ) : (
                                    <Tooltip title="DOWNLOAD" color="blue">
                                      <DownloadOutlined
                                        style={{
                                          color:
                                            documentUrl ===
                                            document?.documentUrl
                                              ? "white"
                                              : "#273881",
                                          fontSize: "0.8rem",
                                        }}
                                        onClick={() =>
                                          handleDownloadDocument?.(
                                            document?.documentName,
                                            document?.documentUrl
                                          )
                                        }
                                      />
                                    </Tooltip>
                                  )}
                                </div>
                              </div>
                            )
                          )}
                        </Col>
                      </Row>
                    </TabPane>
                  ) : null
              )}
            </Tabs>

            {isPdf ? (
              <iframe
                key={documentKey}
                title={documentKey || "PDF Viewer"}
                style={{ height: "108vh", marginTop: "2rem" }}
                src={documentUrl}
                width="100%"
                height="100%"
              />
            ) : (
              <div
                style={{
                  overflow: "auto",
                  width: "100%",
                  height: "108vh",
                  display: "flex",
                  justifyContent: "center",
                  // alignItems: "center",
                  alignItems: "flex-start",
                  marginTop: "2rem",
                }}
              >
                <img
                  key={documentKey}
                  src={documentUrl}
                  style={{
                    transform: `scale(${zoomLevel}) rotate(${rotationAngle}deg)`,
                    transition: "transform 0.3s ease",
                    maxWidth: "none",
                    maxHeight: "none",
                    width: "100%",
                    height: "auto",
                  }}
                  // style={{
                  //   transform: `scale(${zoomLevel}) rotate(${rotationAngle}deg)`,
                  //   transformOrigin: "top center",
                  //   transition: "transform 0.3s ease",
                  //   maxWidth: "100%",
                  //   maxHeight: "100%",
                  // }}
                  alt="Document preview"
                />
              </div>
            )}
          </Modal>
        </div>
      )}
    </>
  );
};

export default PatientListPreviewDocuments;
